import { useCallback, useEffect, useRef } from 'react';

import {
  POPULAR_MOVIES,
  SEARCH_MOVIE,
  TMDB_URL,
  TOP_RATED_MOVIES,
  UPCOMING_MOVIES,
} from '../../utils/constants';
import {
  updateNowPlayingMovies,
  updatePopularMovies,
  updateSearchMovies,
  updateTopRatedMovies,
  updateUpcomingMovies,
} from '../../store/slices/moviesSlice';
import { MovieResponse, MovieType } from '../../types';
import useFetchMovies from '../useFetchMovies';
import { RootState } from '../../store/appStore';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';

const useGetMovies = (urlStr = '', searchText = '') => {
  const [fetchMovies, { data, isLoading, hasMore }] =
    useFetchMovies<MovieResponse>();

  const movies = useAppSelector((store: RootState) => store.movies);
  let movieList: MovieType[] = [];
  let currentPage = 1;
  if (urlStr === POPULAR_MOVIES.url) {
    const { popularMovies } = movies;
    movieList = popularMovies.movieList;
    currentPage = popularMovies.currentPage;
  } else if (urlStr === TOP_RATED_MOVIES.url) {
    const { topRatedMovies } = movies;
    movieList = topRatedMovies.movieList;
    currentPage = topRatedMovies.currentPage;
  } else if (urlStr === UPCOMING_MOVIES.url) {
    const { upcomingMovies } = movies;
    movieList = upcomingMovies.movieList;
    currentPage = upcomingMovies.currentPage;
  } else if (urlStr === SEARCH_MOVIE.url) {
    const { searchMovies } = movies;
    movieList = searchMovies.movieList;
    currentPage = searchMovies.currentPage;
  } else {
    const { nowPlayingMovies } = movies;
    movieList = nowPlayingMovies.movieList;
    currentPage = nowPlayingMovies.currentPage;
  }

  const previousValues = useRef({ currentPage, searchText });

  const dispatch = useAppDispatch();
  useEffect(() => {
    let url =
      TMDB_URL +
      urlStr +
      `?page=${currentPage}${searchText ? `&query=${searchText}` : ''}`;
    if (searchText !== previousValues.current.searchText) {
      url =
        TMDB_URL +
        urlStr +
        `?page=1${searchText ? `&query=${searchText}` : ''}`;
    }
    fetchMovies(url);
    previousValues.current = { currentPage, searchText };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchText]);

  useEffect(() => {
    if (!isLoading && data && data.results.length > 0) {
      const { results, total_pages, page, total_results } = data;
      let updatedResult = [];
      if (page === 1) {
        updatedResult = [...results];
      } else {
        updatedResult = [...movieList, ...results];
      }
      const filteredData = updatedResult.filter(
        (value, index, self) =>
          self.findIndex((v) => v.id === value.id) === index
      );
      const updatedData = {
        movieList: filteredData,
        currentPage: page,
        totalPages: total_pages,
        totalMovies: total_results,
      };
      if (urlStr === POPULAR_MOVIES.url) {
        dispatch(updatePopularMovies({ ...updatedData }));
      } else if (urlStr === TOP_RATED_MOVIES.url) {
        dispatch(updateTopRatedMovies({ ...updatedData }));
      } else if (urlStr === UPCOMING_MOVIES.url) {
        dispatch(updateUpcomingMovies({ ...updatedData }));
      } else if (urlStr === SEARCH_MOVIE.url) {
        dispatch(updateSearchMovies({ ...updatedData, searchText }));
      } else {
        dispatch(updateNowPlayingMovies({ ...updatedData }));
      }
    } else if (!isLoading && data && data.results) {
      const updatedData = {
        movieList: [],
        currentPage: 1,
        totalPages: 0,
        totalMovies: 0,
      };
      if (urlStr === SEARCH_MOVIE.url) {
        dispatch(updateSearchMovies({ ...updatedData, searchText }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  let observer = useRef<IntersectionObserver | null>(null);
  let listRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          const updatedData = {
            isPageUpdate: true,
            currentPage: currentPage + 1,
          };
          if (urlStr === POPULAR_MOVIES.url) {
            dispatch(updatePopularMovies({ ...updatedData }));
          } else if (urlStr === TOP_RATED_MOVIES.url) {
            dispatch(updateTopRatedMovies({ ...updatedData }));
          } else if (urlStr === UPCOMING_MOVIES.url) {
            dispatch(updateUpcomingMovies({ ...updatedData }));
          } else if (urlStr === SEARCH_MOVIE.url) {
            dispatch(updateSearchMovies({ ...updatedData, searchText }));
          } else {
            dispatch(updateNowPlayingMovies({ ...updatedData }));
          }
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, hasMore]
  );

  return { isLoading, listRef, movieList };
};

export default useGetMovies;
